import { KeyRound, MoreVertical, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { EmptyState } from '@/components/common/EmptyState'
import { RelativeTime } from '@/components/data-display/RelativeTime'
import { credentialUsage, describeUsage, hostDefaultCredential, isCredentialTemplate } from '@/lib/gitRouting'
import type { DeploymentGitRouting, GitCredential, GitRoutingRule } from '@/types/gitRouting'

export interface GitCredentialsTableProps {
  credentials: GitCredential[]
  isLoading?: boolean
  /** False when this deployment has no credential store: tokens cannot be managed here. */
  available: boolean
  /** True when the store could not answer; never shown as "nothing set". */
  unavailable?: boolean
  rules: GitRoutingRule[]
  deployment?: DeploymentGitRouting
  canManageTokens: boolean
  /** Opens the token dialog; no name means the user picks one. */
  onSetToken: (name?: string) => void
  onRemove: (credential: GitCredential) => void
}

interface Row {
  name: string
  credential?: GitCredential
}

/**
 * The tenant's Git tokens: names, who set them and when, when a service last used them, and which rules
 * refer to them. Never a value (GR1). A name a rule refers to with no token yet is listed as "not set".
 */
export function GitCredentialsTable({
  credentials,
  isLoading,
  available,
  unavailable,
  rules,
  deployment,
  canManageTokens,
  onSetToken,
  onRemove,
}: GitCredentialsTableProps) {
  const rows = buildRows(credentials, rules, deployment)
  const canWrite = canManageTokens && available && !unavailable

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Credentials</CardTitle>
        {canWrite && (
          <Button size="sm" onClick={() => onSetToken()}>
            <Plus className="mr-2 h-4 w-4" />
            Set token
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2" data-testid="git-credentials-skeleton">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : unavailable ? (
          <Alert>
            <AlertTitle>Credential store unavailable</AlertTitle>
            <AlertDescription>
              The platform could not read credentials just now. Nothing has changed: try again in a minute. If it
              persists, contact your platform operator.
            </AlertDescription>
          </Alert>
        ) : !available ? (
          <Alert>
            <AlertTitle>Tokens are managed by your platform operator</AlertTitle>
            <AlertDescription>
              This deployment has no credential store, so tokens cannot be set here. The rules above still decide
              which credential each repository uses.
            </AlertDescription>
          </Alert>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={KeyRound}
            title="No tokens set"
            description="Set a token for each credential your rules name. It is stored encrypted and can never be read back."
            className="py-8"
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Set by</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Used by</TableHead>
                {canWrite && <TableHead className="w-24" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({ name, credential }) => (
                <TableRow key={name} data-testid="git-credential-row">
                  <TableCell className="font-mono text-sm">{name}</TableCell>
                  {credential ? (
                    <>
                      <TableCell>{credential.setBy ?? '—'}</TableCell>
                      <TableCell>{new Date(credential.updatedAt).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {credential.lastUsedAt ? <RelativeTime date={credential.lastUsedAt} /> : 'never'}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell colSpan={3} className="text-destructive">
                      — not set —
                    </TableCell>
                  )}
                  <TableCell className="text-muted-foreground">
                    {describeUsage(credentialUsage(name, rules, deployment)) || '—'}
                  </TableCell>
                  {canWrite && (
                    <TableCell className="text-right">
                      {credential ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label={`Actions for ${name}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onSetToken(name)}>
                              <RefreshCw className="mr-2 h-4 w-4" />
                              Replace token
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onRemove(credential)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => onSetToken(name)}>
                          Set token
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

/** Stored tokens, then every name a rule or default refers to that has none — each name once. */
function buildRows(
  credentials: GitCredential[],
  rules: GitRoutingRule[],
  deployment: DeploymentGitRouting | undefined,
): Row[] {
  const rows = new Map<string, Row>()
  for (const credential of credentials) {
    rows.set(credential.name.toLowerCase(), { name: credential.name, credential })
  }

  const referenced = [
    ...[...rules].sort((a, b) => a.position - b.position).map((r) => r.credentialName),
    ...(deployment?.rules ?? []).map((r) => r.credentialRef ?? hostDefaultCredential(r.provider)),
    ...(deployment?.hostDefaults ?? []).map((h) => hostDefaultCredential(h.provider)),
  ]
  for (const name of referenced) {
    if (!isCredentialTemplate(name) && !rows.has(name.toLowerCase())) {
      rows.set(name.toLowerCase(), { name })
    }
  }

  return [...rows.values()]
}
